import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

const UserSettings: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [plan, setPlan] = useState<string>('free');
  const [keys, setKeys] = useState<{ id: string; name: string | null; prefix: string; created_at: string; revoked_at: string | null }[]>([]);
  const [newKeyName, setNewKeyName] = useState<string>('');
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: userData, error } = await supabase.auth.getUser();
        if (error) throw error;
        const user = userData.user;
        const currentEmail = user?.email || '';
        setEmail(currentEmail);
        setNewEmail(currentEmail);
      } catch (e) {
        setMessage(e instanceof Error ? e.message : 'Failed to load user');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function loadKeys() {
    const { data, error } = await supabase.from('api_keys').select('id, name, prefix, created_at, revoked_at').order('created_at', { ascending: false });
    if (!error) setKeys(data as any);
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  const handleUpdateEmail = async () => {
    setMessage(null);
    try {
      if (!newEmail || newEmail === email) return;
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;
      setEmail(newEmail);
      setMessage('Email update requested. Check your inbox to confirm.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to update email');
    }
  };

  const handleUpdatePassword = async () => {
    setMessage(null);
    try {
      if (!password || password.length < 8) throw new Error('Password must be at least 8 characters');
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setPassword('');
      setMessage('Password updated.');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to update password');
    }
  };

  // Crypto helpers
  async function sha256Hex(s: string): Promise<string> {
    const buf = new TextEncoder().encode(s);
    const digest = await window.crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  }
  function randomBase62(len: number) {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let out = '';
    const arr = new Uint8Array(len);
    window.crypto.getRandomValues(arr);
    for (let i = 0; i < len; i++) out += alphabet[arr[i] % alphabet.length];
    return out;
  }

  const handleCreateApiKey = async () => {
    setMessage(null);
    try {
      const token = `sk_live_${randomBase62(48)}`;
      const prefix = token.slice(0, 10);
      const token_hash = await sha256Hex(token);
      const { error } = await supabase.from('api_keys').insert({ user_id: (await supabase.auth.getUser()).data.user?.id, name: newKeyName || null, prefix, token_hash });
      if (error) throw error;
      await loadKeys();
      setNewKeyName('');
      setGeneratedKey(token);
      setShowKeyModal(true);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to create API key');
    }
  };

  const handleRevokeApiKey = async (id: string) => {
    const { error } = await supabase.from('api_keys').update({ revoked_at: new Date().toISOString() }).eq('id', id);
    if (!error) await loadKeys();
  };

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>User settings</h3>
      {loading ? (
        <div>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>Account</h4>
            <div className="label">Email</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Email" />
              <button className="button" disabled={!newEmail || newEmail === email} onClick={handleUpdateEmail}>Update email</button>
            </div>
            <div className="label" style={{ marginTop: 12 }}>Password</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" />
              <button className="button secondary" disabled={!password} onClick={handleUpdatePassword}>Update password</button>
            </div>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>Plan</h4>
            <div className="label">Current plan</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="input" value={plan} onChange={(e) => setPlan(e.target.value)}>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="team">Team</option>
              </select>
              <button className="button">Change plan</button>
            </div>
            <div style={{ color: 'var(--color-text-muted)', marginTop: 6 }}>Payment integration coming soon.</div>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>API keys</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input className="input" placeholder="Key name (optional)" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />
              <button className="button" onClick={handleCreateApiKey}>Generate API key</button>
            </div>
            <div style={{ marginTop: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0f111a' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Prefix</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Created</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>{k.prefix}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>{k.name || '—'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>{new Date(k.created_at).toLocaleString()}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>{k.revoked_at ? 'revoked' : 'active'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>
                        {!k.revoked_at && <button className="button secondary" onClick={() => handleRevokeApiKey(k.id)}>Revoke</button>}
                      </td>
                    </tr>
                  ))}
                  {keys.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: '8px', color: 'var(--color-text-muted)' }}>No API keys yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {showKeyModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowKeyModal(false)}>
              <div className="card" style={{ padding: 16, width: 560, maxWidth: '90%', background: 'var(--color-surface)' }} onClick={(e) => e.stopPropagation()}>
                <h4 style={{ marginTop: 0 }}>Your new API key</h4>
                <div style={{ color: 'var(--color-text-muted)', marginBottom: 8 }}>Store this key securely. It will be shown only once.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="input" readOnly value={generatedKey || ''} style={{ flex: 1 }} />
                  <button className="button secondary" onClick={async () => { if (generatedKey) await navigator.clipboard.writeText(generatedKey); }}>Copy</button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                  <button className="button" onClick={() => { setShowKeyModal(false); setGeneratedKey(null); }}>Done</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="button secondary" onClick={handleSignOut}>Sign out</button>
          </div>
          {message && <div style={{ color: '#ff6b6b' }}>{message}</div>}
        </div>
      )}
    </div>
  );
};

export default UserSettings;


